"use client";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import clsx from "clsx";
import { memo, useMemo } from "react";
import DatePickerCalendarContent from "./DatePickerCalendarContent";
import DatePickerFlexibleContent from "./DatePickerFlexibleContent";
import DatePickerSingleCalendarContent from "./DatePickerSingleCalendarContent";

const DatePickerContent = memo(
  ({
    mode = "range", // range | single
    hasTabs = true, // true | false
    cheapestFlightsMonthly,
    activeTab,
    setActiveTab,
    value,
    handleDateSelect,
    defaultMonth,
    getPriceByDate,
    isOneOfCheapestDay,
    currency,
    isRoundTrip,
    mobileDepartureClick,
    isFromDate,
    departureDate,
    mobileReturnClick,
    returnDate,
    isMultiCity,
    cheapestFlightsMonthlyByMonth,
    getCheapestFlightsMonthlyByMonth,
    disabled,
    publicHolidays,
    translationKey = "flightSearch",
  }) => {
    const t = useTranslationDetector(translationKey);

    const tabs = useMemo(
      () => [
        { id: "calendar", label: t("calendar") },
        { id: "flexible", label: t("flexibleDates") },
      ],
      [t],
    );

    return (
      <div className="flex h-full w-full flex-col">
        {/* Tab Headers */}
        {cheapestFlightsMonthly && hasTabs && (
          <div className="border-primary-weak flex shrink-0 justify-center border-b px-4 md:border-b-0 md:px-0 md:pt-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  "w-1/2 cursor-pointer border-b-2 px-6 py-2 font-medium transition-colors duration-200 md:w-[200px] md:py-3",
                  activeTab === tab.id
                    ? "text-primary border-primary"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Calendar Tab Content */}
        {activeTab === "calendar" &&
          (mode === "range" ? (
            <DatePickerCalendarContent
              value={value}
              handleDateSelect={handleDateSelect}
              defaultMonth={defaultMonth}
              getPriceByDate={getPriceByDate}
              isOneOfCheapestDay={isOneOfCheapestDay}
              currency={currency}
              isRoundTrip={isRoundTrip}
              mobileDepartureClick={mobileDepartureClick}
              isFromDate={isFromDate}
              departureDate={departureDate}
              mobileReturnClick={mobileReturnClick}
              returnDate={returnDate}
              isMultiCity={isMultiCity}
              publicHolidays={publicHolidays}
              translationKey={translationKey}
            />
          ) : (
            <DatePickerSingleCalendarContent
              value={value}
              handleDateSelect={handleDateSelect}
              defaultMonth={defaultMonth}
              getPriceByDate={getPriceByDate}
              isOneOfCheapestDay={isOneOfCheapestDay}
              currency={currency}
              isRoundTrip={isRoundTrip}
              mobileDepartureClick={mobileDepartureClick}
              isFromDate={isFromDate}
              departureDate={departureDate}
              mobileReturnClick={mobileReturnClick}
              returnDate={returnDate}
              isMultiCity={isMultiCity}
              disabled={disabled}
              publicHolidays={publicHolidays}
              translationKey={translationKey}
            />
          ))}

        {/* Flexible Tab Content */}
        {activeTab === "flexible" && cheapestFlightsMonthly && (
          <DatePickerFlexibleContent
            cheapestFlightsMonthly={cheapestFlightsMonthly}
            cheapestFlightsMonthlyByMonth={cheapestFlightsMonthlyByMonth}
            getCheapestFlightsMonthlyByMonth={getCheapestFlightsMonthlyByMonth}
            currency={currency}
            isRoundTrip={isRoundTrip}
            translationKey={translationKey}
          />
        )}
      </div>
    );
  },
);

DatePickerContent.displayName = "DatePickerContent";

export default DatePickerContent;
